import React, {memo, useMemo, useRef, useEffect, useCallback} from "react"
import PropTypes from "prop-types"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableCell from "@mui/material/TableCell"
import TableContainer from "@mui/material/TableContainer"
import TableHead from "@mui/material/TableHead"
import TableRow from "@mui/material/TableRow"
import Box from "@mui/material/Box"
import {useTheme} from "@mui/material/styles"
import {Stack} from "@mui/material"
import ButtonWithIcon from "../../../../components/Button/Button"
import {ReactComponent as TrashIcon} from "src/v2/assets/icons/trash-icon.svg"
import {ReactComponent as CopyIcon} from "src/v2/assets/icons/copy-icon.svg"
import {ReactComponent as PasteIcon} from "src/v2/assets/icons/paste-icon.svg"

const FIRST_COL_WIDTH = 240
const DATA_COL_WIDTH = 160
const MIN_COLS_ITEMS_TABLE = 7

function useDragScroll() {
    const containerRef = useRef(null)
    const drag = useRef({active: false, startX: 0, startY: 0, scrollLeft: 0, scrollTop: 0})

    useEffect(() => {
        const el = containerRef.current
        if (!el) return

        const onMouseDown = (e) => {
            if (e.button !== 1) return
            e.preventDefault()
            drag.current = {
                active: true,
                startX: e.pageX,
                startY: e.pageY,
                scrollLeft: el.scrollLeft,
                scrollTop: el.scrollTop,
            }
            el.style.cursor = "grabbing"
        }

        const onMouseMove = (e) => {
            if (!drag.current.active) return
            el.scrollLeft = drag.current.scrollLeft - (e.pageX - drag.current.startX)
            el.scrollTop = drag.current.scrollTop - (e.pageY - drag.current.startY)
        }

        const onMouseUp = (e) => {
            if (e.button !== 1) return
            drag.current.active = false
            el.style.cursor = "default"
        }

        el.addEventListener("mousedown", onMouseDown)
        window.addEventListener("mousemove", onMouseMove)
        window.addEventListener("mouseup", onMouseUp)

        return () => {
            el.removeEventListener("mousedown", onMouseDown)
            window.removeEventListener("mousemove", onMouseMove)
            window.removeEventListener("mouseup", onMouseUp)
        }
    }, [])

    return {containerRef}
}

const stickyBorder = (color) =>
    `inset -1px 0 0 ${color}, inset 0 -1px 0 ${color}`

const firstColSx = (theme) => ({
    width: FIRST_COL_WIDTH,
    minWidth: FIRST_COL_WIDTH,
    maxWidth: FIRST_COL_WIDTH,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    backgroundColor: theme.palette.background.primary,
    color: theme.palette.primary.contrastText,
    fontFamily: theme.typography.fontFamily,
    fontWeight: 700,
    fontSize: 12,
    border: "none",
    boxShadow: stickyBorder(theme.palette.secondary.tertiary),
})

const DynamicColumnTable = (
    {
        fields = [],
        items = [],
        actionButtons,
        height,
        clearTable,
        pasteData,
        copyData,
        setItems
    }) => {
    const theme = useTheme()
    const {containerRef} = useDragScroll()
    const totalDataCols = Math.max(items.length, MIN_COLS_ITEMS_TABLE)

    const dataColumns = useMemo(
        () =>
            Array.from({length: totalDataCols}, (_, i) => ({
                field: `col_${i}`,
                headerName: items?.[i]?.[fields[0]?.key] ?? "",
            })),
        [totalDataCols, items, fields]
    )

    const rows = useMemo(
        () =>
            fields.slice(1).map((field, rowIndex) => {
                const row = {id: rowIndex, fieldName: field.label, fieldKey: field.key}
                for (let ci = 0; ci < totalDataCols; ci++) {
                    row[`col_${ci}`] = items?.[ci]?.[field.key] ?? ""
                }
                return row
            }),
        [fields, items, totalDataCols]
    )

    // Impede quebra de linha com Enter — confirma com Enter, cancela com Escape
    const handleCellKeyDown = useCallback((e, rowIndex, colIndex) => {
        if (e.key === "Enter") {
            e.preventDefault()
            e.currentTarget.blur()
        }
        if (e.key === "Escape") {
            e.preventDefault()
            // restaura o conteudo original e sai
            e.currentTarget.innerText = e.currentTarget.dataset.original ?? ""
            e.currentTarget.blur()
        }

        if (e.key === "Tab" && !e.shiftKey) {
            const isEmptyValue = (v) => v == null || String(v).trim() === ""

            const isColumnEmpty = (column) => {
                if (!column) return true
                return fields.every((field) => isEmptyValue(column[field.key]))
            }

            const lastFilledColIndex = (() => {
                for (let i = items.length - 1; i >= 0; i--) {
                    if (!isColumnEmpty(items[i])) return i
                }
                return -1
            })()

            if (colIndex >= lastFilledColIndex) {
                e.preventDefault()

                const currentValue = e.currentTarget.textContent ?? ""
                handleCellChange({rowIndex, colIndex, value: currentValue})

                requestAnimationFrame(() => {
                    const nextCell = containerRef.current?.querySelector(
                        `[data-row-index="${rowIndex}"][data-col-index="${colIndex + 1}"]`
                    )

                    if (nextCell) {
                        nextCell.focus()
                    }
                })
            }
        }

    }, [])

    // guarda o valor original ao entrar na celula para poder cancelar com Escape
    const handleCellFocus = useCallback((e) => {
        e.currentTarget.dataset.original = e.currentTarget.innerText
    }, [])

    const tableMinWidth = FIRST_COL_WIDTH + DATA_COL_WIDTH * totalDataCols
    const border = `1px solid ${theme.palette.secondary.tertiary}`
    const borderShadow = stickyBorder(theme.palette.secondary.tertiary)

    const handleCellChange = useCallback(({rowIndex, colIndex, value}) => {
        const isEmptyValue = (v) => v == null || String(v).trim() === ""

        const isColumnEmpty = (column) => {
            if (!column) return true
            return fields.every((field) => isEmptyValue(column[field.key]))
        }

        const createEmptyColumn = () => {
            const emptyColumn = {}
            fields.forEach((field) => {
                emptyColumn[field.key] = ""
            })
            return emptyColumn
        }

        const newData = [...items]

        while (newData.length < MIN_COLS_ITEMS_TABLE) {
            newData.push(createEmptyColumn())
        }

        if (!newData[colIndex]) {
            newData[colIndex] = createEmptyColumn()
        }

        if (rowIndex === -1) {
            newData[colIndex] = {
                ...newData[colIndex],
                [fields[0].key]: value,
            }
        } else {
            const fieldKey = fields[rowIndex + 1]?.key
            newData[colIndex] = {
                ...newData[colIndex],
                [fieldKey]: value,
            }
        }

        if (!isColumnEmpty(newData[newData.length - 1])) {
            newData.push(createEmptyColumn())
        }

        while (
            newData.length > MIN_COLS_ITEMS_TABLE &&
            isColumnEmpty(newData[newData.length - 1]) &&
            isColumnEmpty(newData[newData.length - 2])
            ) {
            newData.pop()
        }

        setItems(newData)
    }, [fields, items, setItems])

    const handleCellBlur = useCallback((e, rowIndex, colIndex) => {
        const newValue = e.currentTarget.textContent ?? ""
        const originalValue = e.currentTarget.dataset.original ?? ""

        if (newValue === originalValue) return

        handleCellChange({rowIndex, colIndex, value: newValue})
    }, [handleCellChange])

    return (
        <>
            {actionButtons ?
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{mb: 2}}>
                    <ButtonWithIcon icon={<TrashIcon/>} size="md" onClick={clearTable}>
                        LIMPAR
                    </ButtonWithIcon>

                    <Stack direction="row" spacing={2}>
                        <ButtonWithIcon icon={<CopyIcon/>} size="md" onClick={copyData}>
                            COPIAR
                        </ButtonWithIcon>

                        <ButtonWithIcon icon={<PasteIcon/>} size="md" onClick={pasteData}>
                            COLAR
                        </ButtonWithIcon>
                    </Stack>
                </Stack>
                : null
            }

            <Box sx={{border, backgroundColor: theme.palette.background.primary}}>
                <TableContainer
                    ref={containerRef}
                    sx={{
                        height: height,
                        overflow: "auto",
                        cursor: "default",
                        scrollbarWidth: "thin",
                        scrollbarColor: `${theme.palette.secondary.tertiary} transparent`,
                        "&::-webkit-scrollbar": {width: 4, height: 4},
                        "&::-webkit-scrollbar-thumb": {
                            backgroundColor: theme.palette.secondary.tertiary,
                            borderRadius: 2,
                        },
                    }}
                >
                    <Table
                        stickyHeader
                        sx={{minWidth: tableMinWidth, borderCollapse: "collapse"}}
                    >
                        <TableHead>
                            <TableRow>
                                <TableCell
                                    sx={{
                                        ...firstColSx(theme),
                                        position: "sticky",
                                        top: 0,
                                        left: 0,
                                        textTransform: "uppercase",
                                        zIndex: 30,
                                        height: 50,
                                        boxShadow: `inset -1px 0 0 ${theme.palette.secondary.tertiary}, inset 0 -1px 0 ${theme.palette.secondary.tertiary}, inset 1px 0 0 ${theme.palette.secondary.tertiary}`,
                                    }}
                                >
                                    {fields[0]?.label ?? ""}
                                </TableCell>

                                {dataColumns.map((col, colIndex) => (
                                    <TableCell
                                        key={col.field}
                                        contentEditable
                                        suppressContentEditableWarning
                                        onFocus={handleCellFocus}
                                        onBlur={(e) => handleCellBlur(e, -1, colIndex)}
                                        onKeyDown={(e) => handleCellKeyDown(e, -1, colIndex)}
                                        sx={{
                                            zIndex: 20,
                                            width: DATA_COL_WIDTH,
                                            minWidth: DATA_COL_WIDTH,
                                            maxWidth: DATA_COL_WIDTH,
                                            height: 50,
                                            backgroundColor: theme.palette.background.primary,
                                            color: theme.palette.primary.contrastText,
                                            fontFamily: theme.typography.fontFamily,
                                            fontWeight: 400,
                                            fontSize: 16,
                                            whiteSpace: "nowrap",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            border: "none",
                                            boxShadow: borderShadow,
                                            "&:focus": {
                                                outline: "none",
                                                backgroundColor: theme.palette.input.background.white05,
                                                boxShadow: borderShadow
                                            },
                                        }}
                                    >
                                        {col.headerName}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {rows.map((row, rowIndex) => (
                                <TableRow
                                    key={row.id}
                                    sx={{backgroundColor: theme.palette.background.primary}}
                                >
                                    <TableCell
                                        sx={{
                                            ...firstColSx(theme),
                                            position: "sticky",
                                            textTransform: "uppercase",
                                            left: 0,
                                            zIndex: 10,
                                        }}
                                    >
                                        {row.fieldName}
                                    </TableCell>

                                    {dataColumns.map((col, colIndex) => (
                                        <TableCell
                                            key={col.field}
                                            contentEditable
                                            suppressContentEditableWarning
                                            onFocus={handleCellFocus}
                                            onBlur={(e) => handleCellBlur(e, rowIndex, colIndex)}
                                            onKeyDown={handleCellKeyDown}
                                            sx={{
                                                width: DATA_COL_WIDTH,
                                                minWidth: DATA_COL_WIDTH,
                                                maxWidth: DATA_COL_WIDTH,
                                                whiteSpace: "nowrap",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                color: theme.palette.primary.contrastText,
                                                fontFamily: theme.typography.fontFamily,
                                                fontSize: 16,
                                                fontWeight: 400,
                                                borderRight: border,
                                                borderBottom: border,
                                                cursor: "text",
                                                outline: "none",
                                                "&:focus": {
                                                    backgroundColor: theme.palette.input.background.white05,
                                                },
                                            }}
                                        >
                                            {row[col.field] ?? ""}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        </>
    )
}

DynamicColumnTable.propTypes = {
    fields: PropTypes.arrayOf(
        PropTypes.shape({key: PropTypes.string.isRequired, label: PropTypes.string.isRequired})
    ),
    items: PropTypes.arrayOf(PropTypes.object),
    setItems: PropTypes.func.isRequired,
    onCellChange: PropTypes.func,
    actionButtons: PropTypes.bool,
    height: PropTypes.number,
    clearTable: PropTypes.func,
    pasteData: PropTypes.func,
    copyData: PropTypes.func,
}

export default memo(DynamicColumnTable)